import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { useDropzone } from "react-dropzone";
import {
  applyCheckListRateImport,
  applyCheckListReprice,
  listCheckListRateSheets,
  previewCheckListRateImport,
  previewCheckListReprice,
} from "../../API";
import { STATUS_LABEL } from "../../utils/checklistStatus";
import "../../assets/styles/rate-import-wizard-modal.scss";

const STEP = {
  UPLOAD: "upload",
  IMPORT_REVIEW: "import_review",
  IMPORT_RESULT: "import_result",
  REPRICE_SCOPE: "reprice_scope",
  REPRICE_REVIEW: "reprice_review",
  REPRICE_RESULT: "reprice_result",
};

const STEP_TITLE = {
  [STEP.UPLOAD]: "Import rates — Upload",
  [STEP.IMPORT_REVIEW]: "Import rates — Review",
  [STEP.IMPORT_RESULT]: "Import rates — Result",
  [STEP.REPRICE_SCOPE]: "Reprice checklists — Scope",
  [STEP.REPRICE_REVIEW]: "Reprice checklists — Review",
  [STEP.REPRICE_RESULT]: "Reprice checklists — Result",
};

// The ten rate columns, in server order. Both price and hours share short labels, so the
// table groups them under a two-tier header rather than repeating "Price"/"Hours" in every
// label.
const RATE_COLUMNS = [
  { key: "price_no_modifier", label: "No Mod", group: "Price" },
  { key: "price_12kv_street", label: "12kV St", group: "Price" },
  { key: "price_12kv_easement", label: "12kV Esmt", group: "Price" },
  { key: "price_35kv_street", label: "35kV St", group: "Price" },
  { key: "price_35kv_easement", label: "35kV Esmt", group: "Price" },
  { key: "hours_no_modifier", label: "No Mod", group: "Hours" },
  { key: "hours_12kv_street", label: "12kV St", group: "Hours" },
  { key: "hours_12kv_easement", label: "12kV Esmt", group: "Hours" },
  { key: "hours_35kv_street", label: "35kV St", group: "Hours" },
  { key: "hours_35kv_easement", label: "35kV Esmt", group: "Hours" },
];

const REPRICE_COLUMNS = [
  { key: "price", label: "Price" },
  { key: "crew_price", label: "Crew price" },
  { key: "crew_point", label: "Crew points" },
];

// The dropzone's maxSize must track the server's default — 5 MB until someone changes that
// env key.
const MAX_FILE_BYTES = 5 * 1024 * 1024;

// react-dropzone's hidden file input matches every other FOCUSABLE_SELECTOR rule but should
// never receive auto-focus, so it's excluded explicitly.
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  'input:not([disabled]):not([type="file"])',
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const HEADING_ID = "rateImportWizardModalLabel";
const ERROR_ID = "rateImportWizardModalError";
const SHEET_SELECT_ID = "rateImportWizardSheetSelect";

// Final wins if present (trim-insensitive); otherwise the first visible sheet; otherwise the
// first sheet of all.
const pickDefaultSheet = (sheets) => {
  const final = sheets.find((s) => s.name.trim().toLowerCase() === "final");
  if (final) return final.name;
  const visible = sheets.find((s) => !s.hidden);
  return (visible || sheets[0]).name;
};

// Visible sheets keep workbook order; hidden ones are appended after, in workbook order.
const orderSheetOptions = (sheets) => [
  ...sheets.filter((s) => !s.hidden),
  ...sheets.filter((s) => s.hidden),
];

// `null` and `0` are distinct on both sides of every comparison — render a stored null as a
// dash, never as 0 and never as a blank cell indistinguishable from a zero.
const displayValue = (value) => (value === null || value === undefined ? "—" : value);

const formatChangeText = (oldValues, newValues, key) =>
  `${displayValue(oldValues[key])} → ${displayValue(newValues[key])}`;

const valueChanged = (oldValues, newValues, key) => oldValues[key] !== newValues[key];

// A tile of one summary count. value is nullish while a delta hasn't loaded yet.
const Tile = ({ value, label }) => (
  <div className="rate-import-wizard-modal__tile">
    <span className="rate-import-wizard-modal__tile-value">{value ?? 0}</span>
    <span>{label}</span>
  </div>
);

// The shared old→new delta table for both the import review and reprice review steps.
// `groups` (optional) renders a two-tier header, e.g. Price/Hours over the rate columns;
// without it, leadColumns and columns share a single header row.
const leadCellStyle = (leadColumns, index) => ({
  left: leadColumns.slice(0, index).reduce((sum, lead) => sum + lead.width, 0),
  minWidth: leadColumns[index].width,
  maxWidth: leadColumns[index].width,
});

const DeltaTable = ({ leadColumns, columns, rows, rowKey, groups }) => {
  const groupRowRef = useRef(null);
  const [groupRowHeight, setGroupRowHeight] = useState(0);

  useLayoutEffect(() => {
    setGroupRowHeight(groupRowRef.current ? groupRowRef.current.getBoundingClientRect().height : 0);
  }, [groups, columns]);

  return (
  <div className="rate-import-wizard-modal-table-wrap">
    <table className="rate-import-wizard-modal-table">
      <thead>
        {groups ? (
          <tr ref={groupRowRef}>
            {leadColumns.map((lead, index) => (
              <th
                key={lead.key}
                rowSpan={2}
                scope="col"
                className="rate-import-wizard-modal-table__lead"
                style={leadCellStyle(leadColumns, index)}
              >
                {lead.label}
              </th>
            ))}
            {groups.map((group) => (
              <th key={group.name} colSpan={group.count} scope="colgroup">{group.name}</th>
            ))}
          </tr>
        ) : null}
        <tr>
          {!groups
            ? leadColumns.map((lead, index) => (
                <th
                  key={lead.key}
                  scope="col"
                  className="rate-import-wizard-modal-table__lead"
                  style={leadCellStyle(leadColumns, index)}
                >
                  {lead.label}
                </th>
              ))
            : null}
          {columns.map((column) => (
            <th key={column.key} scope="col" style={groups ? { top: groupRowHeight } : undefined}>
              {column.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={rowKey(row)}>
            {leadColumns.map((lead, index) => (
              <td
                key={lead.key}
                className="rate-import-wizard-modal-table__lead"
                style={leadCellStyle(leadColumns, index)}
              >
                {lead.render(row)}
              </td>
            ))}
            {columns.map((column) => (
              <td
                key={column.key}
                className={
                  valueChanged(row.old, row.new, column.key)
                    ? "rate-import-wizard-modal-table__cell--changed"
                    : undefined
                }
              >
                {formatChangeText(row.old, row.new, column.key)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  );
};

const IMPORT_TABLE_LEAD_COLUMNS = [
  { key: "service_number", label: "Service #", width: 88, render: (row) => row.service_number },
  { key: "description", label: "Description", width: 260, render: (row) => row.description },
];

const IMPORT_TABLE_GROUPS = [
  { name: "Price", count: 5 },
  { name: "Hours", count: 5 },
];

const REPRICE_TABLE_LEAD_COLUMNS = [
  { key: "service_number", label: "Service #", width: 88, render: (row) => row.service_number },
  { key: "quantity", label: "Qty", width: 56, render: (row) => row.quantity },
];

// `name` is the discriminator — never branch on message text. A resolved response with a
// non-200 status (a 500/504/404-not-deployed/unregistered 4xx) is also a failure; both
// applies are idempotent, so pointing the operator at re-running the preview is always safe
// advice.
const errorFromResponse = (err) => {
  const data = err?.response?.data;
  return {
    message: data?.message || "Something went wrong. Re-run the preview to see current state.",
    details: Array.isArray(data?.details) ? data.details : undefined,
  };
};

const nonSuccessError = () => ({
  message: "That didn't complete. Re-run the preview to see current state.",
});

/**
 * The six-step rate import / reprice wizard. Mechanics copied from StatusChangeNotesModal.js:
 * own backdrop div (not a portal), role="dialog", a Tab trap inside the dialog, Escape
 * cancels (no-op while busy), focus restored to the trigger on close, submittingRef
 * double-submit guard, inline alert-danger error region.
 *
 * The wizard never parses, rounds or prices anything client-side — it renders exactly what
 * the four endpoints return, and re-posts the same File on apply rather than anything parsed
 * from the preview.
 *
 * Props:
 * - isOpen:      whether the dialog is shown
 * - onClose:     () => void — dismiss without necessarily having applied anything
 * - onCompleted: () => void — called once, after a successful apply, so the caller can
 *                 refresh the checklist grid
 * - triggerRef:  optional ref to the button that opened the wizard, focused back on close
 */
const RateImportWizardModal = ({ isOpen, onClose, onCompleted = () => {}, triggerRef }) => {
  const [step, setStep] = useState(STEP.UPLOAD);
  const [file, setFile] = useState(null);
  const [sheet, setSheet] = useState("");
  const [sheetOptions, setSheetOptions] = useState(null);
  const [sheetsLoading, setSheetsLoading] = useState(false);
  const [scope, setScope] = useState("unbilled");
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [importDelta, setImportDelta] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [repriceDelta, setRepriceDelta] = useState(null);
  const [repriceResult, setRepriceResult] = useState(null);

  const dialogRef = useRef(null);
  const bodyRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      previouslyFocusedElementRef.current = document.activeElement;
      setStep(STEP.UPLOAD);
      setFile(null);
      setSheet("");
      setSheetOptions(null);
      setSheetsLoading(false);
      setScope("unbilled");
      setServerError(null);
      setImportDelta(null);
      setImportResult(null);
      setRepriceDelta(null);
      setRepriceResult(null);
      setBusy(false);
      submittingRef.current = false;
      return undefined;
    }

    const restoreTarget = triggerRef?.current || previouslyFocusedElementRef.current;
    if (restoreTarget instanceof HTMLElement) restoreTarget.focus();
    previouslyFocusedElementRef.current = null;
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Focus the first control of the active step's own content, not the header close button
  // or the footer actions — bodyRef only wraps the step's fields/tables.
  useEffect(() => {
    if (!isOpen) return undefined;
    const timer = setTimeout(() => {
      const target = bodyRef.current?.querySelector(FOCUSABLE_SELECTOR) || dialogRef.current;
      target?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [isOpen, step]);

  const fetchSheets = async (droppedFile) => {
    setSheetOptions(null);
    setSheet("");
    setSheetsLoading(true);
    try {
      const sheetFormData = new FormData();
      sheetFormData.append("file", droppedFile);
      const res = await listCheckListRateSheets(sheetFormData);
      if (res?.status === 200) {
        const sheets = res.data?.sheets || [];
        setSheetOptions(sheets);
        setSheet(pickDefaultSheet(sheets));
      } else {
        setServerError(nonSuccessError());
      }
    } catch (err) {
      setServerError(errorFromResponse(err));
    } finally {
      setSheetsLoading(false);
    }
  };

  const onDrop = (acceptedFiles, rejectedFiles) => {
    setServerError(null);
    if (rejectedFiles?.length > 0) {
      const details = rejectedFiles[0].errors?.map((e) => e.message);
      setServerError({ message: "That file can't be used.", details });
      return;
    }
    if (acceptedFiles?.length > 0) {
      const droppedFile = acceptedFiles[0];
      setFile(droppedFile);
      fetchSheets(droppedFile);
    }
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    multiple: false,
    maxSize: MAX_FILE_BYTES,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    },
  });

  const handleCancel = () => {
    if (busy || submittingRef.current) return;
    onClose();
  };

  // Any close after a successful import apply refreshes the grid — whether it's "Not now"
  // on step 3 or Close on step 6 after a reprice apply too.
  const handleFinish = () => {
    if (busy || submittingRef.current) return;
    onClose();
    if (importResult) onCompleted();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      handleCancel();
      return;
    }

    if (event.key === "Tab") {
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  const buildImportFormData = () => {
    const formData = new FormData();
    formData.append("file", file);
    const trimmedSheet = sheet.trim();
    if (trimmedSheet) formData.append("sheet", trimmedSheet);
    return formData;
  };

  const handlePreviewImport = async () => {
    if (busy || submittingRef.current || !file || !sheet) return;
    submittingRef.current = true;
    setBusy(true);
    setServerError(null);
    try {
      const res = await previewCheckListRateImport(buildImportFormData());
      if (res?.status === 200) {
        setImportDelta(res.data);
        setStep(STEP.IMPORT_REVIEW);
      } else {
        setServerError(nonSuccessError());
      }
    } catch (err) {
      setServerError(errorFromResponse(err));
    } finally {
      setBusy(false);
      submittingRef.current = false;
    }
  };

  const handleApplyImport = async () => {
    if (busy || submittingRef.current || importDelta?.summary?.invalid > 0) return;
    submittingRef.current = true;
    setBusy(true);
    setServerError(null);
    try {
      const res = await applyCheckListRateImport(buildImportFormData());
      if (res?.status === 200) {
        setImportResult(res.data);
        setStep(STEP.IMPORT_RESULT);
      } else {
        setServerError(nonSuccessError());
      }
    } catch (err) {
      setServerError(errorFromResponse(err));
    } finally {
      setBusy(false);
      submittingRef.current = false;
    }
  };

  const handlePreviewReprice = async () => {
    if (busy || submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    setServerError(null);
    try {
      const res = await previewCheckListReprice(scope);
      if (res?.status === 200) {
        setRepriceDelta(res.data);
        setStep(STEP.REPRICE_REVIEW);
      } else {
        setServerError(nonSuccessError());
      }
    } catch (err) {
      setServerError(errorFromResponse(err));
    } finally {
      setBusy(false);
      submittingRef.current = false;
    }
  };

  const handleApplyReprice = async () => {
    if (busy || submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    setServerError(null);
    try {
      const res = await applyCheckListReprice(scope);
      if (res?.status === 200) {
        setRepriceResult(res.data);
        setStep(STEP.REPRICE_RESULT);
      } else {
        setServerError(nonSuccessError());
      }
    } catch (err) {
      setServerError(errorFromResponse(err));
    } finally {
      setBusy(false);
      submittingRef.current = false;
    }
  };

  if (!isOpen) return null;

  const summary = importDelta?.summary;
  const repriceGroups = [];
  if (repriceDelta?.changes) {
    const byChecklist = new Map();
    repriceDelta.changes.forEach((change) => {
      const key = change.project_check_list_id;
      if (!byChecklist.has(key)) {
        byChecklist.set(key, {
          project_check_list_id: key,
          work_order_number: change.work_order_number,
          pole_number: change.pole_number,
          status: change.status,
          rows: [],
        });
        repriceGroups.push(byChecklist.get(key));
      }
      byChecklist.get(key).rows.push(change);
    });
  }

  return (
    <>
      <div className="modal-backdrop show rate-import-wizard-modal-backdrop" />
      <div
        className="modal fade show d-block rate-import-wizard-modal"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby={HEADING_ID}
        aria-describedby={serverError ? ERROR_ID : undefined}
        data-bs-backdrop="static"
        onKeyDown={handleKeyDown}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg" ref={dialogRef}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id={HEADING_ID}>
                {STEP_TITLE[step]}
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                disabled={busy}
                onClick={handleCancel}
              />
            </div>

            <div className="modal-body" ref={bodyRef}>
              {serverError ? (
                <div className="alert alert-danger" role="alert" id={ERROR_ID}>
                  <p className="rate-import-wizard-modal__error-message">{serverError.message}</p>
                  {serverError.details?.length > 0 ? (
                    <ul className="rate-import-wizard-modal__error-details">
                      {serverError.details.map((line, index) => (
                        <li key={index}>{line}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}

              {step === STEP.UPLOAD ? (
                <div className="rate-import-wizard-modal__step">
                  <div {...getRootProps()} className="rate-import-wizard-modal__dropzone">
                    <input {...getInputProps()} />
                    <p>Drag and drop the rate workbook here, or browse for it.</p>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={open}
                      disabled={busy}
                    >
                      Browse files
                    </button>
                    {file ? (
                      <p className="rate-import-wizard-modal__file-name">{file.name}</p>
                    ) : null}
                    <p className="rate-import-wizard-modal__hint">
                      .xlsx only, up to {Math.round(MAX_FILE_BYTES / (1024 * 1024))} MB.
                    </p>
                  </div>

                  <div aria-busy={sheetsLoading}>
                    <label htmlFor={SHEET_SELECT_ID}>Sheet</label>
                    {sheetsLoading ? (
                      <p className="rate-import-wizard-modal__hint">Reading sheets…</p>
                    ) : null}
                    {sheetOptions?.length > 0 ? (
                      <select
                        id={SHEET_SELECT_ID}
                        className="form-control"
                        value={sheet}
                        onChange={(event) => setSheet(event.target.value)}
                      >
                        {orderSheetOptions(sheetOptions).map((option) => (
                          <option key={option.name} value={option.name}>
                            {option.hidden ? `${option.name} (hidden)` : option.name}
                          </option>
                        ))}
                      </select>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {step === STEP.IMPORT_REVIEW ? (
                <div className="rate-import-wizard-modal__step">
                  <div className="rate-import-wizard-modal__tiles" aria-live="polite">
                    <Tile value={summary?.changed} label="Changed" />
                    <Tile value={summary?.unchanged} label="Unchanged" />
                    <Tile value={summary?.missing} label="Missing" />
                    <Tile value={summary?.invalid} label="Invalid" />
                  </div>

                  {summary?.changed === 0 ? (
                    <p className="rate-import-wizard-modal__empty">
                      No rates in this sheet differ from the catalog. Nothing will be written.
                    </p>
                  ) : (
                    <DeltaTable
                      leadColumns={IMPORT_TABLE_LEAD_COLUMNS}
                      groups={IMPORT_TABLE_GROUPS}
                      columns={RATE_COLUMNS}
                      rows={importDelta.changed}
                      rowKey={(row) => row.service_number}
                    />
                  )}

                  {importDelta?.missing?.length > 0 ? (
                    <div className="rate-import-wizard-modal__note">
                      <p>
                        Service numbers with no catalog row (not created by this import — add
                        them through the existing Add Checklist flow, then re-run the import):
                      </p>
                      <p>{importDelta.missing.join(", ")}</p>
                    </div>
                  ) : null}

                  {importDelta?.invalid?.length > 0 ? (
                    <div className="rate-import-wizard-modal__note">
                      <p>Rows that cannot be imported — fix the sheet and re-upload:</p>
                      <ul>
                        {importDelta.invalid.map((row) => (
                          <li key={row.row}>Row {row.row}: {row.reason}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {step === STEP.IMPORT_RESULT ? (
                <div className="rate-import-wizard-modal__step" aria-live="polite">
                  <p>{importResult?.updated ?? 0} rows updated.</p>
                  <p>Reprice existing checklists now?</p>
                </div>
              ) : null}

              {step === STEP.REPRICE_SCOPE ? (
                <div className="rate-import-wizard-modal__step">
                  <fieldset>
                    <legend>Scope</legend>
                    <div className="form-check">
                      <input
                        type="radio"
                        id="rate-import-scope-unbilled"
                        name="rate-import-reprice-scope"
                        className="form-check-input"
                        value="unbilled"
                        checked={scope === "unbilled"}
                        onChange={() => setScope("unbilled")}
                      />
                      <label htmlFor="rate-import-scope-unbilled" className="form-check-label">
                        Unbilled
                      </label>
                      <p className="rate-import-wizard-modal__hint">
                        Includes approved-but-unbilled checklists — their amounts will change.
                      </p>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        id="rate-import-scope-unbilled-unapproved"
                        name="rate-import-reprice-scope"
                        className="form-check-input"
                        value="unbilled_unapproved"
                        checked={scope === "unbilled_unapproved"}
                        onChange={() => setScope("unbilled_unapproved")}
                      />
                      <label htmlFor="rate-import-scope-unbilled-unapproved" className="form-check-label">
                        Unbilled, not yet approved
                      </label>
                    </div>
                  </fieldset>
                </div>
              ) : null}

              {step === STEP.REPRICE_REVIEW ? (
                <div className="rate-import-wizard-modal__step">
                  <div className="rate-import-wizard-modal__tiles" aria-live="polite">
                    <Tile value={repriceDelta?.summary?.items_total} label="Items total" />
                    <Tile value={repriceDelta?.summary?.items_changed} label="Items changed" />
                    <Tile
                      value={repriceDelta?.summary?.items_skipped_no_modifier}
                      label="Skipped — no modifier"
                    />
                    <Tile
                      value={repriceDelta?.summary?.items_skipped_no_rate}
                      label="Skipped — no rate"
                    />
                    <Tile
                      value={repriceDelta?.summary?.checklists_affected}
                      label="Checklists affected"
                    />
                  </div>

                  {repriceGroups.map((group) => (
                    <div key={group.project_check_list_id} className="rate-import-wizard-modal__group">
                      <h6 className="rate-import-wizard-modal__group-title">
                        {group.work_order_number || "—"} — {group.pole_number || "—"} —{" "}
                        {STATUS_LABEL[group.status] ?? "—"}
                      </h6>
                      <DeltaTable
                        leadColumns={REPRICE_TABLE_LEAD_COLUMNS}
                        columns={REPRICE_COLUMNS}
                        rows={group.rows}
                        rowKey={(row) => row.item_id}
                      />
                    </div>
                  ))}
                </div>
              ) : null}

              {step === STEP.REPRICE_RESULT ? (
                <div className="rate-import-wizard-modal__step" aria-live="polite">
                  <p>{repriceResult?.updated ?? 0} item prices updated.</p>
                </div>
              ) : null}
            </div>

            <div className="modal-footer">
              {step === STEP.UPLOAD ? (
                <>
                  <button type="button" className="btn btn-secondary" disabled={busy} onClick={handleCancel}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="primary-btn"
                    disabled={!file || !sheet || busy}
                    onClick={handlePreviewImport}
                  >
                    {busy ? "Previewing…" : "Preview"}
                  </button>
                </>
              ) : null}

              {step === STEP.IMPORT_REVIEW ? (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={busy}
                    onClick={() => setStep(STEP.UPLOAD)}
                  >
                    Back
                  </button>
                  <div>
                    {summary?.invalid > 0 ? (
                      <span className="rate-import-wizard-modal__corrective-text">
                        Fix the {summary.invalid} invalid row{summary.invalid === 1 ? "" : "s"} and re-upload.
                      </span>
                    ) : null}
                    <button
                      type="button"
                      className="primary-btn"
                      disabled={busy || summary?.invalid > 0}
                      onClick={handleApplyImport}
                    >
                      {busy ? "Importing…" : "Import"}
                    </button>
                  </div>
                </>
              ) : null}

              {step === STEP.IMPORT_RESULT ? (
                <>
                  <button type="button" className="btn btn-secondary" disabled={busy} onClick={handleFinish}>
                    Not now
                  </button>
                  <button
                    type="button"
                    className="primary-btn"
                    disabled={busy}
                    onClick={() => setStep(STEP.REPRICE_SCOPE)}
                  >
                    Yes
                  </button>
                </>
              ) : null}

              {step === STEP.REPRICE_SCOPE ? (
                <>
                  <button type="button" className="btn btn-secondary" disabled={busy} onClick={handleFinish}>
                    Cancel
                  </button>
                  <button type="button" className="primary-btn" disabled={busy} onClick={handlePreviewReprice}>
                    {busy ? "Previewing…" : "Preview reprice"}
                  </button>
                </>
              ) : null}

              {step === STEP.REPRICE_REVIEW ? (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={busy}
                    onClick={() => setStep(STEP.REPRICE_SCOPE)}
                  >
                    Back
                  </button>
                  <button type="button" className="primary-btn" disabled={busy} onClick={handleApplyReprice}>
                    {busy ? "Repricing…" : "Reprice"}
                  </button>
                </>
              ) : null}

              {step === STEP.REPRICE_RESULT ? (
                <button type="button" className="primary-btn" onClick={handleFinish}>
                  Close
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RateImportWizardModal;
